/**
 * (c) 2024, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import {
  ControlledModalProps,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Text,
  VStack,
} from "@microbit/ui";

import { FormattedMessage } from "react-intl";
import { AnimationProvider } from "./AnimationProvider";
import { ButtonWithLoading } from "./ButtonWithLoading";
import { useConnectFirst } from "./ConnectFirstDialog";
import HowItWorksAnimation from "./HowItWorksAnimation/index";
import PauseResumeLink from "./PauseResumeAnimationLink";

type WelcomeDialogProps = Omit<ControlledModalProps, "children">;

const WelcomeDialog = ({ onClose, isOpen, ...rest }: WelcomeDialogProps) => {
  const { handleClose, isConnecting, handleConnect } = useConnectFirst({
    isOpen,
    onClose,
  });

  return (
    <AnimationProvider startPausedIfReducedMotion>
      <Modal
        isDismissable={false}
        motionless
        size={{ base: "full", sm: "full", md: "4xl" }}
        isCentered
        onClose={handleClose}
        isOpen={isOpen}
        {...rest}
      >
        <ModalHeader>
          <Text>
            <FormattedMessage id="welcome-title" />
          </Text>
        </ModalHeader>
        <ModalBody>
          <ModalCloseButton />
          <HowItWorksAnimation />
        </ModalBody>
        <ModalFooter css={{ justifyContent: "space-between" }}>
          <VStack alignItems="start">
            <PauseResumeLink />
          </VStack>
          <ButtonWithLoading
            variant="primary"
            onClick={handleConnect}
            isLoading={isConnecting}
          >
            <FormattedMessage id="connect-action" />
          </ButtonWithLoading>
        </ModalFooter>
      </Modal>
    </AnimationProvider>
  );
};

export default WelcomeDialog;
